import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Lock, Smartphone, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { useAuth } from '../../contexts/AuthContext';

const AdminLogin = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    totpCode: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await login(
        formData.email, 
        formData.password, 
        requires2FA ? formData.totpCode : null
      );

      if (result.requires2FA) {
        setRequires2FA(true);
        setLoading(false);
        return;
      }

      if (result.success) {
        navigate('/dashboard/fondateur');
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo et titre */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold mb-2">
            <div className="padaid-logo inline">
              <span className="pa-red">PA</span>
              <span className="daid-green">DAID</span>
            </div> Admin
          </h1>
          <p className="text-muted-foreground">
            Connexion sécurisée avec authentification à double facteur
          </p>
        </div>

        <Card className="border-border/50">
          <CardHeader>
            <h2 className="text-xl font-semibold text-center flex items-center justify-center">
              <Lock className="h-5 w-5 mr-2" />
              {requires2FA ? 'Authentification 2FA' : 'Connexion fondateur'}
            </h2>
          </CardHeader>
          
          <CardContent>
            {error && (
              <Alert className="mb-4" variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleLogin} className="space-y-4">
              {!requires2FA ? (
                <>
                  <div>
                    <Label htmlFor="email">Adresse email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="email fondateur"
                      required
                      autoComplete="email"
                    />
                  </div>

                  <div>
                    <Label htmlFor="password">Mot de passe</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                        placeholder="Votre mot de passe"
                        required
                        autoComplete="current-password"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="text-center mb-4">
                    <Smartphone className="h-12 w-12 text-primary mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">
                      Entrez le code à 6 chiffres de votre application d'authentification
                      (Google Authenticator, Authy, etc.)
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="totpCode">Code d'authentification</Label>
                    <Input
                      id="totpCode"
                      type="text"
                      value={formData.totpCode}
                      onChange={(e) => handleInputChange('totpCode', e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="123456"
                      required
                      className="text-center text-lg tracking-widest"
                      maxLength={6}
                      autoComplete="one-time-code"
                    />
                  </div>

                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      setRequires2FA(false);
                      setFormData(prev => ({ ...prev, totpCode: '' }));
                    }}
                    className="w-full"
                  >
                    ← Retour à la connexion
                  </Button>
                </>
              )}

              <Button 
                type="submit" 
                className="w-full bg-primary hover:bg-primary/90"
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-foreground mr-2"></div>
                    Connexion...
                  </div>
                ) : requires2FA ? (
                  'Vérifier le code'
                ) : (
                  'Se connecter'
                )}
              </Button>
            </form>

            {!requires2FA && (
              <div className="mt-6 text-center">
                <p className="text-sm text-muted-foreground">
                  Besoin d'aide ?
                </p>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/login')}
                  className="text-primary p-0 h-auto"
                >
                  Utiliser la page Connexion standard
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="text-center mt-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/auth')}
            className="text-muted-foreground hover:text-foreground"
          >
            ← Retour à l'authentification
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;